import { generateClient } from 'aws-amplify/api';
import '../config/amplifyConfig.js'; // Ensure Amplify is configured

/**
 * AI Service for smart service matching using OpenAI GPT-4o-mini
 * Falls back to rule-based analysis when AI is unavailable
 */
export default class AIService {
  static getClient() {
    if (!this._client) {
      this._client = generateClient({
        authMode: 'apiKey' // Use public API key for unauthenticated access
      });
    }
    return this._client;
  }

  /**
   * Analyze user input and suggest appropriate services using OpenAI
   * @param {string} userInput - Natural language description of user's problem
   * @param {string} countryCode - User's country for context
   * @param {Function} traditionalSearchFn - Function to call traditional search when AI fails
   * @returns {Promise<Object>} AI analysis with service suggestions
   */
  static async analyzeServiceRequest(userInput, countryCode = 'syria', traditionalSearchFn = null) {
    try {
      // Call our OpenAI Lambda function via GraphQL query
      const response = await this.getClient().graphql({
        query: `
          query AnalyzeServiceRequest($userInput: String!, $countryCode: String) {
            analyzeServiceRequest(userInput: $userInput, countryCode: $countryCode) {
              confidence
              primaryService
              category
              reasoning
              alternativeServices
              suggestedSpecialties
            }
          }
        `,
        variables: {
          userInput: userInput,
          countryCode: countryCode
        }
      });

      if (response?.data?.analyzeServiceRequest) {
        const aiResult = response.data.analyzeServiceRequest;
        return this.validateAndEnhanceAnalysis(aiResult, userInput);
      }

      // If Lambda call fails, try traditional search first
      return await this.fallbackToTraditionalSearch(userInput, traditionalSearchFn);

    } catch (error) {
      console.error('AIService: Error analyzing service request:', error);
      
      // Try traditional search as fallback
      return await this.fallbackToTraditionalSearch(userInput, traditionalSearchFn);
    }
  }

  /**
   * Fallback to traditional search when AI is unavailable
   * @param {string} userInput - User's input
   * @param {Function} traditionalSearchFn - Traditional search function
   * @returns {Promise<Object>} Analysis based on traditional search or rule-based fallback
   */
  static async fallbackToTraditionalSearch(userInput, traditionalSearchFn) {
    // If traditional search function is provided, try it first
    if (traditionalSearchFn) {
      try {
        const traditionalResults = await traditionalSearchFn(userInput);
        
        if (Array.isArray(traditionalResults) && traditionalResults.length > 0) {
          const topResult = traditionalResults[0];
          const serviceId = topResult.service_id || topResult.id || topResult.serviceId;
          
          if (serviceId) {
            // Create analysis based on traditional search result
            return this.validateAndEnhanceAnalysis({
              confidence: 0.6, // Moderate confidence for traditional search
              primaryService: serviceId,
              category: topResult.category_id || 'home_maintenance',
              reasoning: 'تم العثور على هذه الخدمة من خلال البحث التقليدي',
              alternativeServices: traditionalResults.slice(1, 3).map(r => r.service_id || r.id || r.serviceId).filter(Boolean),
              suggestedSpecialties: [],
              isFallback: true,
              isTraditionalSearch: true
            }, userInput);
          }
        }
        
      } catch (error) {
        console.error('Traditional search failed:', error);
      }
    }
    
    // Final fallback to rule-based analysis
    const analysis = this.createFallbackAnalysis(userInput);
    return this.validateAndEnhanceAnalysis(analysis, userInput);
  }

  /**
   * Create a fallback analysis using rule-based matching
   * @param {string} userInput - User's input
   * @returns {Object} Fallback analysis
   */
  static createFallbackAnalysis(userInput) {
    const input = userInput.toLowerCase();
    
    // Rule-based keyword matching with enhanced Arabic support
    const rules = [
      // Checked before construction: these terms (ترميم، تعهدات، إكساء، مقاول) used to resolve
      // to construction, and now belong to the admin-brokered renovation service.
      {
        keywords: ['renovation', 'refurbishment', 'rehabilitation', 'restoration', 'remodeling', 'general contracting', 'contracting', 'contractor', 'general contractor', 'turnkey', 'إكساء', 'اكساء', 'الإكساء', 'الاكساء', 'إعادة تأهيل', 'اعادة تاهيل', 'تأهيل', 'ترميم', 'ترميم بناء', 'تجديد', 'التعهدات العامة', 'تعهدات عامة', 'تعهد', 'تعهدات', 'متعهد', 'متعهد عام', 'مقاول', 'مقاولات', 'مقاول عام', 'مقاولات عامة', 'تشطيب', 'إعادة بناء'],
        service: 'renovation',
        category: 'home_maintenance',
        confidence: 0.9,
        specialties: ['renovation_contracting']
      },
      {
        keywords: ['leaky', 'leak', 'faucet', 'tap', 'pipe', 'water', 'plumbing', 'تمديدات الصحية', 'تسريب', 'حنفية', 'أنبوب', 'مياه', 'صنبور', 'خرطوم', 'مرحاض', 'حمام', 'دش'],
        service: 'plumbing_repair',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['fix_leaky_faucets_pipes', 'repair_replace_toilets', 'unclog_drains', 'water_heater_repair']
      },
      {
        keywords: ['electrical', 'electric', 'power', 'outlet', 'switch', 'light', 'wiring', 'كهرباء', 'كهربائي', 'مقبس', 'إضاءة', 'تمديد', 'الضوء', 'الكهربا', 'مقطوعة', 'انقطاع', 'عطل', 'يعمل', 'لا يعمل', 'مفتاح', 'لمبة', 'ضوء'],
        service: 'electrical_repair',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['fix_outlets_switches', 'electrical_wiring_repair', 'lighting_fixture_repair', 'circuit_breaker_installation']
      },
      {
        keywords: ['clean', 'cleaning', 'dirty', 'mess', 'تنظيف', 'نظافة', 'وسخ', 'نظف', 'تنظيف المنزل', 'غسيل'],
        service: 'house_cleaning',
        category: 'home_maintenance',
        confidence: 0.7,
        specialties: ['deep_cleaning_entire_home', 'kitchen_bathroom_sanitization']
      },
      {
        keywords: ['carpet', 'rug', 'سجاد', 'موكيت', 'زربية'],
        service: 'house_cleaning',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['carpet_rug_cleaning']
      },
      {
        keywords: ['paint', 'painting', 'wall', 'دهان', 'طلاء', 'جدار', 'حائط', 'داخلي', 'خارجي', 'سقف'],
        service: 'painting_molding',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['interior_wall_painting', 'exterior_wall_painting', 'ceiling_painting']
      },
      {
        keywords: ['garden', 'lawn', 'tree', 'plant', 'landscaping', 'حديقة', 'عشب', 'شجرة', 'زراعة', 'بستنة', 'نباتات'],
        service: 'gardening_landscaping',
        category: 'outdoor_services',
        confidence: 0.7,
        specialties: ['lawn_mowing_trimming', 'planting_flowers_shrubs', 'tree_trimming_removal']
      },
      {
        keywords: ['furniture', 'chair', 'table', 'cabinet', 'أثاث', 'كرسي', 'طاولة', 'خزانة', 'إصلاح', 'تصليح', 'تنجيد'],
        service: 'upholstery_furniture',
        category: 'home_maintenance',
        confidence: 0.7,
        specialties: ['sofa_reupholstery', 'wooden_furniture_repair', 'chair_cushion_replacement']
      },
      {
        keywords: ['solar', 'panels', 'شمسي', 'ألواح', 'طاقة شمسية'],
        service: 'solar_power',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['rooftop_solar_installation', 'panel_repair', 'solar_maintenance']
      },
      {
        keywords: ['carpenter', 'wood', 'cabinet', 'نجارة', 'نجار', 'خشب', 'أخشاب', 'صنع', 'تركيب', 'باب', 'نافذة'],
        service: 'carpentry',
        category: 'home_maintenance',
        confidence: 0.7,
        specialties: ['custom_furniture', 'cabinet_shelving_installation', 'build_repair_doors_windows']
      },
      // TEMPORARILY DISABLED SERVICES (moving_hauling, pest_control_service,
      // vehicle_repair_maintenance). They are `isActive: false` in appData, so nothing may be
      // assigned to them any more. The three rules are merged into one that answers
      // unspecified at a low confidence, which keeps the request general instead. The block
      // keeps the position the moving rule had, so first-match ordering against the other
      // rules is unchanged. Restore the three original rules when the services come back.
      {
        keywords: ['move', 'moving', 'transport', 'نقل', 'شحن', 'ترحيل',
                   'pest', 'bug', 'insect', 'cockroach', 'حشرات', 'قوارض', 'صراصير', 'بق', 'تبخير',
                   'car', 'auto', 'vehicle', 'سيارة', 'سيارات', 'مركبة', 'إصلاح سيارة', 'ميكانيك'],
        service: 'unspecified',
        category: 'unspecified',
        confidence: 0.3,
        specialties: ['general_consultation']
      },
      {
        keywords: ['air conditioning', 'ac', 'تكييف', 'مكيف', 'تبريد', 'تدفئة'],
        service: 'air_conditioning',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['install_new_ac_units', 'repair_ac_compressors', 'recharge_refrigerant_gas']
      },
      {
        keywords: ['floor', 'tile', 'flooring', 'أرضية', 'بلاط', 'سيراميك', 'تبليط'],
        service: 'flooring',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['install_ceramic_tiles', 'install_marble_granite_flooring', 'repair_cracked_tiles']
      },
      {
        keywords: ['appliance', 'refrigerator', 'washing machine', 'oven', 'ثلاجة', 'غسالة', 'فرن', 'أجهزة منزلية'],
        service: 'appliance_repair',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['refrigerator_compressor_repair', 'washing_machine_motor_repair', 'oven_heating_element_replacement']
      },
      {
        keywords: ['blacksmith', 'metalwork', 'welding', 'gate', 'fence', 'railing', 'balcony', 'حدادة', 'حديد', 'لحام', 'بوابة', 'سور', 'دربزون', 'بلكون'],
        service: 'blacksmith_metalwork',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['gate_fence_repair_manufacturing', 'window_door_metalwork', 'security_bars_installation', 'welding_services']
      },
      {
        keywords: ['glass', 'mirror', 'window glass', 'broken glass', 'زجاج', 'مرآة', 'زجاج النافذة', 'زجاج مكسور', 'بلور', 'شبابيك', 'مراي', 'دش'],
        service: 'glass_repair',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['window_glass_replacement', 'broken_glass_repair', 'shower_glass_installation', 'mirror_installation']
      },
      {
        keywords: ['aluminum', 'aluminium', 'aluminum window', 'aluminum door', 'facade', 'ألومنيوم', 'الومنيوم', 'نافذة ألومنيوم', 'باب ألومنيوم', 'واجهة', 'شبابيك ألومنيوم'],
        service: 'aluminum_work',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['aluminum_windows_installation', 'aluminum_doors_installation', 'storefront_facade_work', 'aluminum_repairs_maintenance']
      },
      {
        keywords: ['construction', 'building', 'build', 'brick', 'concrete', 'بناء', 'تشييد', 'إنشاء', 'عمارة', 'بناء منازل', 'إعمار', 'تزريق', 'طيانة', 'أنقاض', 'هدم', 'واجهة حجر', 'خرسانة', 'آجر'],
        service: 'construction',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['bricklaying_wall_construction', 'cement_mortar_work', 'structural_crack_inspection']
      },
      {
        keywords: ['satellite', 'dish', 'lnb', 'receiver', 'signal', 'antenna', 'dsh', 'دش', 'طبق', 'قمر صناعي', 'ساتلايت', 'ريسيفر', 'إشارة', 'صحن', 'إصلاح دش', 'تركيب دش', 'إصلاح صحن', 'تركيب صحن', 'رأس الاستقبال', 'كابل', 'إشارة ضعيفة'],
        service: 'satellite_dish',
        category: 'home_maintenance',
        confidence: 0.8,
        specialties: ['satellite_dish_installation', 'satellite_dish_repair', 'dish_alignment_tuning', 'lnb_replacement', 'signal_troubleshooting']
      }
    ];

    // Find matching rules
    for (const rule of rules) {
      const hasKeyword = rule.keywords.some(keyword => input.includes(keyword));
      if (hasKeyword) {
        return {
          confidence: rule.confidence,
          primaryService: rule.service,
          category: rule.category,
          reasoning: `تم اختيار هذه الخدمة بناءً على وصف المشكلة`,
          alternativeServices: [],
          suggestedSpecialties: rule.specialties,
          isAIGenerated: false,
          isFallback: true
        };
      }
    }

    // Default fallback
    return {
      confidence: 0.3,
      primaryService: 'unspecified',
      category: 'unspecified',
      reasoning: 'لم يتم تحديد خدمة محددة، يرجى وصف الطلب بمزيد من التفصيل',
      alternativeServices: ['unspecified'],
      suggestedSpecialties: ['general_consultation'],
      isAIGenerated: false,
      isFallback: true
    };
  }

  /**
   * Validate and enhance AI analysis response
   * @param {Object} analysis - Raw AI analysis
   * @param {string} originalInput - Original user input
   * @returns {Object} Validated and enhanced analysis
   */
  static validateAndEnhanceAnalysis(analysis, originalInput) {
    // Define valid services and categories
    // moving_hauling / pest_control_service / vehicle_repair_maintenance are intentionally
    // absent: those services are disabled (isActive: false in appData), so an analysis naming
    // one of them is rewritten to 'unspecified' below and the request stays general.
    const validServices = [
      'plumbing_repair', 'solar_power', 'electrical_repair', 'carpentry', 'construction', 'renovation',
      'appliance_repair', 'electronic_repair', 'smart_home', 'painting_molding',
      'house_cleaning', 'blacksmith_metalwork', 'glass_repair', 'aluminum_work', 
      'air_conditioning', 'flooring', 'satellite_dish',
      'upholstery_furniture', 'gardening_landscaping', 
      'elevator_services', 'unspecified'
    ];

    const validCategories = [
      'home_maintenance', 'outdoor_services', 'unspecified'
    ];

    // Validate and set defaults
    const enhanced = {
      confidence: Math.max(0, Math.min(1, analysis.confidence || 0.5)),
      primaryService: validServices.includes(analysis.primaryService) ? analysis.primaryService : 'unspecified',
      category: validCategories.includes(analysis.category) ? analysis.category : 'unspecified',
      reasoning: analysis.reasoning || 'AI analysis of user request',
      alternativeServices: (analysis.alternativeServices || []).filter(service => validServices.includes(service)),
      suggestedSpecialties: analysis.suggestedSpecialties || [],
      originalInput: originalInput,
      isAIGenerated: !analysis.isFallback, // True if from AI, false if from fallback
      isFallback: analysis.isFallback || false,
      isTraditionalSearch: analysis.isTraditionalSearch || false, // True when using traditional search as fallback
      timestamp: new Date().toISOString()
    };

    // Ensure we have some alternative services if confidence is low (but not for unspecified)
    if (enhanced.confidence < 0.7 && enhanced.alternativeServices.length === 0 && enhanced.primaryService !== 'unspecified') {
      enhanced.alternativeServices = ['plumbing_repair', 'electrical_repair', 'house_cleaning']
        .filter(service => service !== enhanced.primaryService)
        .slice(0, 2);
    }

    return enhanced;
  }



  /**
   * Get display text for service
   * @param {string} serviceId - Service ID
   * @returns {string} Display text in Arabic
   */
  static getServiceDisplayText(serviceId) {
    const serviceTexts = {
      'plumbing_repair': 'تمديدات الصحية',
      'solar_power': 'الطاقة الشمسية',
      'electrical_repair': 'إصلاح الكهرباء',
      'carpentry': 'نجارة',
      'construction': 'بناء',
      'renovation': 'الإكساء والتعهدات',
      'appliance_repair': 'إصلاح أدوات منزلية',
      'electronic_repair': 'إصلاح الإلكترونيات',
      'smart_home': 'المنزل الذكي',
      'painting_molding': 'دهان وقوالب',
      'house_cleaning': 'تنظيف المنازل',
      'blacksmith_metalwork': 'الحدادة والأعمال المعدنية',
      'glass_repair': 'إصلاح وتركيب الزجاج',
      'aluminum_work': 'أعمال الألومنيوم',
      'air_conditioning': 'تكييف',
      'flooring': 'تبليط',
      'moving_hauling': 'نقل أثاث',
      'pest_control_service': 'مكافحة الحشرات و القوارض',
      'vehicle_repair_maintenance': 'إصلاح السيارات',
      'upholstery_furniture': 'تنجيد وأثاث',
      'gardening_landscaping': 'بستنة وتنسيق حدائق',
      'satellite_dish': 'إصلاح وتركيب صحون الأقمار الصناعية',
      'elevator_services': 'خدمات وتركيب المصاعد',
      'unspecified': 'خدمات أخرى'
    };

    return serviceTexts[serviceId] || serviceId;
  }

  /**
   * Track AI analysis for improvement
   * @param {Object} analysis - AI analysis result
   * @param {string} userAction - What the user did (accepted, modified, rejected)
   * @param {string} finalServiceId - Final service the user selected
   */
  static async trackAnalysisResult(analysis, userAction, finalServiceId = null) {
    try {
      // Log for analytics and improvement
      const trackingData = {
        originalInput: analysis.originalInput,
        aiSuggestion: analysis.primaryService,
        confidence: analysis.confidence,
        userAction: userAction,
        finalSelection: finalServiceId,
        wasCorrect: finalServiceId === analysis.primaryService,
        timestamp: new Date().toISOString()
      };

      // In production, send this data to analytics service
    } catch (error) {
      console.error('AIService: Error tracking analysis result:', error);
    }
  }
}
